import { isEqual } from 'lodash';
import {
	Tooth,
	ToothRestoration,
	ToothSurface,
} from '../../../graphql/graphql';

const isEmpty = (
	item: { surfaces?: unknown[]; values?: Record<string, unknown> } | null,
) =>
	!item ||
	((item.surfaces?.length ?? 0) === 0 &&
		!Object.keys(item.values ?? {}).length);

const filterEmpty = (tooth?: Tooth) => {
	if (!tooth) {
		return tooth;
	}

	const decays = tooth.pathology?.decays?.filter(d => !isEmpty(d));
	const fillings = tooth.restoration?.fillings?.filter(f => !isEmpty(f));

	return {
		...tooth,
		pathology: tooth.pathology && {
			...tooth.pathology,
			decays: decays?.length ? decays : undefined,
		},
		restoration: tooth.restoration && {
			...tooth.restoration,
			fillings: fillings?.length ? fillings : undefined,
		},
	};
};

const normalizeSurfaces = (surfaces?: ToothSurface[]) => {
	if (Array.isArray(surfaces) && surfaces.length === 0) {
		return null;
	}
	return surfaces ?? null;
};

const normalizeRestoration = (restoration?: ToothRestoration) => {
	if (!restoration) {
		return restoration;
	}

	const normalized: Partial<ToothRestoration> = {};
	(Object.keys(restoration) as Array<keyof ToothRestoration>).forEach(key => {
		if (restoration[key] != null && key !== 'type') {
			normalized[key] = restoration[key];
		}
	});

	return normalized;
};

const isEmptyRestoration = (r?: ToothRestoration) =>
	!r || (!(r.fillings?.length || r.surfaces?.length) && !r.type);

const compareRestoration = (
	current?: ToothRestoration,
	original?: ToothRestoration,
) => {
	if (isEmptyRestoration(original) && isEmptyRestoration(current)) {
		return true;
	}

	if (!original || !current) {
		return false;
	}

	const { surfaces: originalSurfaces, ...originalRestoration } = original;
	const { surfaces: currentSurfaces, ...currentRestoration } = current;

	const normalizedOriginalSurfaces = normalizeSurfaces(originalSurfaces);
	const normalizedCurrentSurfaces = normalizeSurfaces(currentSurfaces);

	if (!isEqual(normalizedCurrentSurfaces, normalizedOriginalSurfaces)) {
		return false;
	}

	return isEqual(
		normalizeRestoration(currentRestoration),
		normalizeRestoration(originalRestoration),
	);
};

export default function hasToothChanges(
	activeTooth?: Tooth,
	originalTooth?: Tooth,
) {
	if (!activeTooth || !originalTooth) {
		return false;
	}

	const normalizedActiveTooth = filterEmpty(activeTooth);
	const normalizedOriginalTooth = filterEmpty(originalTooth);

	if (!normalizedActiveTooth || !normalizedOriginalTooth) {
		return false;
	}

	return (
		!isEqual(
			normalizedActiveTooth.pathology,
			normalizedOriginalTooth.pathology,
		) ||
		!compareRestoration(
			normalizedActiveTooth.restoration,
			normalizedOriginalTooth.restoration,
		) ||
		!isEqual(
			normalizedActiveTooth.periodontal,
			normalizedOriginalTooth.periodontal,
		)
	);
}
